import type {
  FileUploadResponse,
  FileResponse,
  PresignedUrlResponse,
  FileUploadDto,
  FileDeleteResponse,
  FileServeResponse
} from '@/types';
import { UtilsService } from '../utils';
import { authorizedAPI } from '@/config/axios.config';

const utils = new UtilsService();

class FileService {
  // POST /files/upload
  async uploadFile({ file, folder }: FileUploadDto): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('private', 'true');

    if (folder) {
      formData.append('folder', folder);
    }

    return utils.handleApiRequest(() =>
      authorizedAPI.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  }

  // DELETE /files/{id}
  async deleteFile(id: string): Promise<FileDeleteResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.delete(`/files/${id}`)
    );
  }

  // GET /files/{id}
  async getFileById(id: string): Promise<FileResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/${id}`)
    );
  }

  // GET /files/serve/by-id/{id}
  async serveFileById(id: string): Promise<FileServeResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/serve/by-id/${id}`)
    );
  }

  // GET /files/presigned-url/by-id/{id}
  async getPresignedUrlById(id: string): Promise<PresignedUrlResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/presigned-url/by-id/${id}`)
    );
  }

  // GET /files/presigned-url/by-url/{fileUrl}
  async getPresignedUrlByUrl(fileUrl: string): Promise<PresignedUrlResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/presigned-url/by-url/${fileUrl}`)
    );
  }

  // GET /files/serve/signed/{id}
  async serveFileSigned(id: string): Promise<FileServeResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/serve/signed/${id}`)
    );
  }

  // GET /files/serve/{fileUrl}
  async serveFileByUrl(fileUrl: string): Promise<FileServeResponse> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/files/serve/${fileUrl}`)
    );
  }

  // GET /files/download/{id}
  async downloadFileById(id: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await authorizedAPI.get(`/files/download/${id}`, {
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header
      // For blob responses, headers might be in response.headers or response.headers.common
      let filename = id; // fallback to id

      // Try different ways to access headers (axios may normalize them)
      const contentDisposition =
        response.headers?.['content-disposition'] ||
        response.headers?.['Content-Disposition'] ||
        (response.headers as any)?.contentDisposition ||
        (response.headers as any)?.common?.['content-disposition'] ||
        (response.headers as any)?.common?.['Content-Disposition'];

      if (contentDisposition) {
        // Try RFC 5987 format first: filename*=UTF-8''encoded-filename
        let filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        } else {
          // Try quoted format: filename="filename" (handles escaped quotes inside)
          filenameMatch = contentDisposition.match(/filename="((?:[^"\\]|\\.)*)"/i);
          if (filenameMatch && filenameMatch[1]) {
            // Remove escaped quotes and trim
            filename = filenameMatch[1].replace(/\\"/g, '"').replace(/\\'/g, "'").trim();
          } else {
            // Try unquoted format: filename=filename
            filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim().replace(/^["']|["']$/g, ''); // Remove surrounding quotes if any
            }
          }
        }
      } else {
        // Debug: log headers if filename extraction fails
        console.warn('Content-Disposition header not found. Available headers:', Object.keys(response.headers || {}));

        // Fallback: try to get filename from file metadata
        try {
          const fileData = await this.getFileById(id);
          // Backend returns file with 'title' field (from Prisma schema)
          filename = (fileData as any).title || (fileData as any).originalName || (fileData as any).filename || id;
        } catch (metaError) {
          console.warn('Could not fetch file metadata for filename:', metaError);
          // Keep the fallback to id
        }
      }

      return {
        blob: response.data,
        filename,
      };
    } catch (error) {
      // Re-throw to let the caller handle the error
      throw error;
    }
  }
}

export const fileService = new FileService();
